import { useEffect, useMemo, useState } from 'react';
import { Modal, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { PeriodSelection, RecomputeDiff, RecomputeDiffLine } from '@salaries/shared';
import { awaitRunJob, recomputeApply, type RecomputeApplyResult } from '../../api/payrollRun';
import { Money } from '../../components/Money';

const STATUS_LABEL: Record<string, string> = {
  draft: 'טרם נשלח',
  sent: 'נשלח',
  approved: 'אושר',
  final: 'סופי',
};

// Corrections on a locked run: preview the Arbox-vs-frozen diff, pick which lines
// to re-freeze, apply. Protected (sent/approved/final) changed lines are
// unchecked by default with a warning — applying voids their approval.
// The diff is computed in the background (a full Arbox rebuild) and handed in
// once ready — this modal never fetches, so it never shows a minutes-long
// spinner. It only opens when there is something to show.
export function RecomputeDiffModal({
  open,
  selection,
  diff,
  unitId,
  onClose,
}: {
  open: boolean;
  selection: PeriodSelection;
  diff: RecomputeDiff | null;
  /** The SAME P&L-unit scope the diff was built with (recomputePreview's own
   *  `unitId`) — apply must stay within it too, or checking a coach's TA row
   *  in this diff could silently also re-freeze their (never-shown) הרצליה
   *  line. Omitted = every currently-locked unit, matching the combined view. */
  unitId?: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [checked, setChecked] = useState<number[]>([]);

  // Default selection: changed & unprotected lines.
  useEffect(() => {
    if (diff) {
      setChecked(
        diff.lines.filter((l) => l.changed && !l.protected).map((l) => l.arbox_users_boxes_id),
      );
    }
  }, [diff]);

  // Applying is another full rebuild. Queue it, close the modal, and let the
  // operator get on with their day — the outcome arrives as a toast.
  const applyMutation = useMutation({
    mutationFn: () => recomputeApply(selection, checked, unitId),
    onSuccess: () => {
      onClose();
      toast('מעדכנים את השורות שנבחרו — נעדכן כשיהיה מוכן.', { icon: '⏳', duration: 7000 });
      void awaitRunJob<RecomputeApplyResult>(selection, 'recompute_apply', { unitId })
        .then((r) => {
          toast.success(
            `עודכנו ${r?.applied ?? 0} שורות${r?.voided ? ` (${r.voided} אישורים בוטלו)` : ''}`,
          );
          void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
        })
        .catch((err) => toast.error((err as Error).message || 'שגיאה בעדכון'));
    },
    onError: (err) => toast.error((err as Error).message || 'שגיאה בעדכון'),
  });

  const changedLines = useMemo(() => (diff?.lines ?? []).filter((l) => l.changed), [diff]);

  const columns: ColumnsType<RecomputeDiffLine> = [
    {
      title: 'שם',
      dataIndex: 'display_name',
      render: (name: string, r) =>
        r.protected ? (
          <span>
            <Tooltip title="שורה שכבר נשלחה/אושרה — עדכון יבטל את האישור">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="text-warning-yellow"
                style={{ marginInlineEnd: 6 }}
              />
            </Tooltip>
            {name}
          </span>
        ) : (
          name
        ),
    },
    {
      title: 'סטטוס',
      dataIndex: 'status',
      width: 90,
      render: (s: string) => STATUS_LABEL[s] ?? s,
    },
    { title: 'לפני', dataIndex: 'before_total', align: 'right', render: (v) => <Money value={v} /> },
    { title: 'אחרי', dataIndex: 'after_total', align: 'right', render: (v) => <Money value={v} /> },
    {
      title: 'שינוי',
      key: 'delta',
      align: 'right',
      render: (_, r) => {
        // When only the ניכוי מס במקור rate moved, the invoice totals are equal
        // and the real change is in the net — show that delta instead.
        const netBefore = r.before_net_total ?? r.before_total;
        const netAfter = r.after_net_total ?? r.after_total;
        const totalsEqual = Math.round((r.after_total - r.before_total) * 100) === 0;
        const d = totalsEqual
          ? Math.round((netAfter - netBefore) * 100) / 100
          : Math.round((r.after_total - r.before_total) * 100) / 100;
        if (d === 0) return <span className="text-secondary-text">—</span>;
        return (
          <span className={d > 0 ? 'text-success-green' : 'text-error-red'} dir="ltr">
            {d > 0 ? '+' : ''}
            {d.toLocaleString()}
          </span>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      title="חישוב מחדש מול Arbox"
      okText={`עדכן ${checked.length} שורות`}
      cancelText="ביטול"
      okButtonProps={{ disabled: checked.length === 0, loading: applyMutation.isPending }}
      onOk={() => applyMutation.mutate()}
    >
      {!diff ? (
        <div className="text-secondary-text py-6">שגיאה בטעינת השוואה.</div>
      ) : changedLines.length === 0 ? (
        <div className="text-main-text py-6">אין שינויים מול הנתונים הקפואים. הכל מעודכן. ✅</div>
      ) : (
        <>
          <div className="text-small text-secondary-text mb-3">
            {diff.changed_count} שורות השתנו מאז סגירת התקופה
            {diff.protected_changed_count > 0 && (
              <span className="text-warning-yellow">
                {' '}
                · {diff.protected_changed_count} מתוכן כבר נשלחו/אושרו (סימון יבטל את האישור)
              </span>
            )}
            . בחר אילו לעדכן:
          </div>
          <Table<RecomputeDiffLine>
            rowKey="arbox_users_boxes_id"
            size="small"
            columns={columns}
            dataSource={changedLines}
            pagination={false}
            scroll={{ y: 360 }}
            rowSelection={{
              selectedRowKeys: checked,
              onChange: (keys) => setChecked(keys.map(Number)),
            }}
          />
        </>
      )}
    </Modal>
  );
}
